import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Users, Globe, PackageCheck, Target, ChevronDown, Check, PackageX } from 'lucide-react';
const services = [{
  id: 'sourcing',
  icon: Search,
  title: 'Product Sourcing for Retail Chains',
  description: 'We help hypermarkets and large retail groups identify reliable manufacturers, source high-quality goods at competitive prices, and manage the entire procurement process.',
  details: 'Our sourcing strategy focuses on cost efficiency, product quality, and supply chain reliability. We understand the standards, compliance requirements, packaging expectations, and volume demands of large-scale retail operations.',
  benefits: ['Manufacturer Identification', 'Competitive Pricing', 'Supply Contracts', 'Bulk Purchasing']
}, {
  id: 'representation',
  icon: Users,
  title: 'Manufacturers & Producers Representation',
  description: 'We represent manufacturers seeking access to high-volume retail markets across the UK, Europe, and Asia.',
  details: 'As your commercial representative, we present your product portfolio to retail chains, negotiate listing agreements, facilitate product approvals, manage compliance documentation, and coordinate buyer presentations.',
  benefits: ['Retail Buyer Access', 'Listing Negotiations', 'Product Approvals', 'Compliance Support']
}, {
  id: 'market-entry',
  icon: Globe,
  title: 'International Market Entry & Expansion',
  description: 'We assist companies in introducing their products into new markets through research, distribution channels, and regulatory support.',
  details: 'Entering a new country requires more than supply — it requires strategy. We conduct market research, identify distribution channels, connect with importers and retail buyers, and manage product registration processes.',
  benefits: ['Market Research', 'Distribution Channels', 'Regulatory Compliance', 'Launch Strategy']
}, {
  id: 'procurement',
  icon: PackageCheck,
  title: 'Procurement & Supply Management',
  description: 'Beyond sourcing, we actively manage price negotiations, contract structuring, order consolidation, and international trade logistics.',
  details: 'We operate as a trusted intermediary, reducing risk, saving time, and improving margins for our retail partners. From quality control coordination to shipping logistics, we handle it all.',
  benefits: ['Price Negotiation', 'Order Consolidation', 'Quality Control', 'Trade Documentation']
}, {
  id: 'marketing',
  icon: Target,
  title: 'Marketing & Product Promotion',
  description: 'We help brands build visibility and demand through product launch coordination, in-store promotions, and strategic market positioning.',
  details: 'Our objective is not only to place your product on shelves — but to help it succeed and grow. We coordinate retail launches, adapt branding for local markets, and negotiate shelf placement.',
  benefits: ['Launch Coordination', 'In-Store Promotions', 'Brand Adaptation', 'Shelf Placement']
}, {
  id: 'liquidation',
  icon: PackageX,
  title: 'Stock Liquidation & Surplus Solutions',
  description: 'We help manufacturers, distributors, and retailers recover value from excess inventory, discontinued lines, overstock, and near-expiry products.',
  details: 'Our extensive network of wholesalers, discount retailers, secondary markets, and export buyers enables us to move stock quickly while protecting brand value. We handle everything from bulk clearance to confidential, brand-sensitive liquidation.',
  benefits: ['Surplus Purchasing', 'Volume Clearance', 'Near-Expiry Management', 'Brand-Safe Liquidation']
}];
export function ServicesSection() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  return <section id="services" className="py-24 bg-[#0a1628] relative" data-id="element-482">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-483">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-484">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-485">
            <span className="text-[#d4a853] font-medium tracking-[0.2em] uppercase text-sm block mb-4" data-id="element-486">
              Our Core Services
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-487">
              End-to-End Sourcing & Market Solutions
            </h2>
            <p className="text-gray-400 text-lg" data-id="element-488">
              From identifying manufacturers to placing products on retail
              shelves, we manage every step of the supply and expansion process.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-489">
          {services.map((service, index) => <motion.div key={service.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className={`bg-[#1a2d4a]/20 border ${expandedId === service.id ? 'border-[#d4a853]' : 'border-white/10'} p-8 transition-colors duration-300 cursor-pointer group`} onClick={() => setExpandedId(expandedId === service.id ? null : service.id)} data-id="element-490">
              <div className="flex items-start justify-between mb-4" data-id="element-491">
                <div className={`p-3 rounded-sm transition-colors duration-300 ${expandedId === service.id ? 'bg-[#d4a853]' : 'bg-[#1a2d4a] group-hover:bg-[#d4a853]'}`} data-id="element-492">
                  <service.icon className={`w-6 h-6 transition-colors duration-300 ${expandedId === service.id ? 'text-[#0a1628]' : 'text-[#d4a853] group-hover:text-[#0a1628]'}`} data-id="element-493" />
                </div>
                <motion.div animate={{
              rotate: expandedId === service.id ? 180 : 0
            }} transition={{
              duration: 0.3
            }} data-id="element-494">
                  <ChevronDown className={`w-6 h-6 ${expandedId === service.id ? 'text-[#d4a853]' : 'text-gray-500'}`} data-id="element-495" />
                </motion.div>
              </div>

              <h3 className={`text-xl font-bold mb-3 transition-colors ${expandedId === service.id ? 'text-[#d4a853]' : 'text-white'}`} data-id="element-496">
                {service.title}
              </h3>

              <p className="text-gray-400 leading-relaxed mb-4" data-id="element-497">
                {service.description}
              </p>

              <AnimatePresence data-id="element-498">
                {expandedId === service.id && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-499">
                    <div className="pt-4 border-t border-white/10 mt-4" data-id="element-500">
                      <p className="text-gray-300 mb-6 text-sm leading-relaxed" data-id="element-501">
                        {service.details}
                      </p>
                      <div className="grid grid-cols-2 gap-3" data-id="element-502">
                        {service.benefits.map((benefit, i) => <div key={i} className="flex items-center gap-2 text-sm text-gray-400" data-id="element-503">
                            <Check className="w-4 h-4 text-[#d4a853]" data-id="element-504" />
                            <span data-id="element-505">{benefit}</span>
                          </div>)}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </div>
    </section>;
}